import Sugar from 'sugar';

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { ContextChangeController } from '../ns-services/context-change.js';
import {
  nsDirectoryServiceContext,
  ltnAuthServiceContext,
  nsWorkflowServiceContext,
  ltnDialogServiceContext,
  ltnToastServiceContext,
  nsCommuncationServiceContext,
  nsLoaderContext,
  nsSettingsServiceContext,
  nsDirectoryStateContext,
} from '../ns-services/contexts.js';

import { ButtressDbService, ButtressEntity, buttressDbServiceContext } from '@buttress/crag';

import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import { LtnDialogService, DialogInputType, ErrorDialogClosed } from '../ltn-dialog/ltn-dialog-service.js';
import { LtnToastService } from '../ltn-toast/ltn-toast-service.js';

import { LtnAuthService } from '../ltn-auth/ltn-auth-service.js';
import { NSWorkflowService } from '../ns-workflows/ns-workflow-service.js';
import { NSCommuncationService } from '../ns-services/ns-communication-service.js';
import { NSLoader } from '../ns-services/ns-loader-service.js';
import { nsFlyoutStyles, nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';
import { viewPath } from '../helpers/route.js';

import { NSSettingsService } from '../ns-settings/ns-settings-service.js';
import { NSDirectoryService } from '../ns-services/ns-directory-service.js';

import '../ns-elements/ns-test-persona-switcher.js';
import {
  createLogger,
  LOG_LEVELS,
  type LogLevel,
  getLogLevel,
  isLoggingEnabled,
  setLogLevel,
  setLoggingEnabled,
} from '../ns-services/logger.js';

import Person from '../models/person.js';
import { callLambda } from '../helpers/lambda.js';
import { NOTIFICATION_PRIORITIES, isNotificationPriority } from '../models/notification.js';
import type { NotificationPriority } from '../models/notification.js';
import { PublicLedgerTopic } from '../models/public-ledger.js';

const logger = createLogger('ns-dev-tools-panel');

class ErrorDialogInvalid extends Error {}

/**
 * Right-hand overlay panel for dev/test/staging-only tooling — build info, session info, a
 * console-logging toggle, and the test-persona switcher, with room for more tools alongside them.
 */
@customElement(NSDevToolsPanel.is)
export class NSDevToolsPanel extends LitElement {
  static get is() {
    return 'ns-dev-tools-panel';
  }

  @property({ type: Boolean, reflect: true })
  open = false;

  @consume({ context: nsDirectoryServiceContext })
  private __dir?: NSDirectoryService;

  private __profileChanges = new ContextChangeController(this, nsDirectoryStateContext, () =>
    this.__updateSessionInfo(),
  );

  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  @consume({ context: ltnDialogServiceContext })
  private __dialog?: LtnDialogService;

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  @consume({ context: nsWorkflowServiceContext })
  private __workflowService?: NSWorkflowService;

  @consume({ context: nsCommuncationServiceContext })
  private __communcation?: NSCommuncationService;

  @consume({ context: nsLoaderContext })
  private __loader?: NSLoader;

  private __internalPlTopic?: PublicLedgerTopic;

  @state()
  private __appMode?: string;

  @state()
  private __appVersion?: string;

  @state()
  private __buildDate?: string;

  @state()
  private __personId?: string;

  @state()
  private __companyId?: string;

  @state()
  private __companyName?: string;

  @state()
  private __loggingEnabled: boolean = isLoggingEnabled();

  @state()
  private __logLevel: LogLevel = getLogLevel();

  @state()
  private __testNotificationPriority: NotificationPriority = 'NORMAL';

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    nsFlyoutStyles,
    css`
      :host {
        --ns-flyout-closed-transform: translateX(100%);
        --ns-flyout-open-transform: translateX(0);
        --ns-flyout-origin: 50% 50%;

        top: 0;
        right: 0;
        height: 100%;
        width: 100%;
        max-width: 400px;
        box-sizing: border-box;
        overflow: hidden;

        background: var(--ns-color-inverse-surface);
        color: var(--ns-color-inverse-text);

        overflow-y: auto;
      }

      .wrapper {
        padding: var(--wa-space-m);
      }

      h2 {
        margin: 0 0 var(--wa-space-m);
        font-size: var(--wa-font-size-m);
        font-weight: 600;
        line-height: 100%;
        letter-spacing: -0.03125rem;
        color: var(--ns-color-inverse-text);
      }

      section {
        margin-bottom: var(--wa-space-m);
        padding: var(--wa-space-m);
        border-radius: var(--wa-border-radius-l);
        background: var(--ns-theme-black);
      }

      h3 {
        margin: 0 0 var(--wa-space-xs);
        font-size: var(--wa-font-size-xs);
        font-weight: 400;
        line-height: 100%;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        opacity: 0.7;
      }

      .panel-select {
        --wa-form-control-label-color: var(--ns-color-inverse-text);

        width: 100%;
        margin-top: var(--wa-space-s);
      }

      .panel-select::part(form-control-label) {
        position: static;
        width: auto;
        height: auto;
        margin: 0 0 var(--wa-space-2xs);
        overflow: visible;
        clip: auto;
        white-space: normal;
      }

      .row {
        display: flex;
        justify-content: space-between;
        gap: var(--wa-space-xs);
        font-size: var(--wa-font-size-s);
        padding: var(--wa-space-3xs) 0;
        margin: 0;
      }

      .row .value {
        opacity: 0.8;
        text-align: right;
        word-break: break-all;
      }

      label {
        font-size: var(--wa-font-size-s);
        display: flex;
        align-items: center;
        gap: var(--wa-space-xs);
      }

      .actions wa-button {
        --wa-color-fill-loud: var(--ns-theme-grey-dark);

        width: 100%;
        margin-bottom: var(--wa-space-xs);

        font-size: var(--wa-font-size-2xs);
      }

      .actions wa-button:last-child {
        margin-bottom: 0;
      }

      .actions .panel-select {
        margin: 0 0 var(--wa-space-xs);
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.isConnected) {
      logger.debug('Element has been disconnected before finishing the connectedCallback execution');
      return;
    }

    const settings = this.__settings;
    if (settings) {
      const app = settings.settings.app as { mode?: string; version?: string; buildDate?: string } | undefined;
      this.__appMode = app?.mode;
      this.__appVersion = app?.version;
      this.__buildDate = app?.buildDate;
    }

    if (this.__dir) {
      await this.__dir.initialised;
      this.__updateSessionInfo();
    }

    if (this.__db) {
      const internalPLTopicQueryCall = await this.__db.query('plTopic', {
        type: {
          $eq: 'INTERNAL',
        },
      });
      const results = internalPLTopicQueryCall.results as unknown as PublicLedgerTopic[];
      if (results.length > 0) [this.__internalPlTopic] = results;
    }
  }

  private __updateSessionInfo() {
    this.__personId = this.__dir?.me?.id;
    this.__companyId = this.__dir?.company?.id;
    this.__companyName = this.__dir?.company?.name;
  }

  private __toggleLogging() {
    setLoggingEnabled(!isLoggingEnabled());
    this.__loggingEnabled = isLoggingEnabled();
  }

  private __selectLogLevel(ev: Event) {
    const level = (ev.target as HTMLElement & { value: string }).value as LogLevel;
    if (!LOG_LEVELS.includes(level) || level === this.__logLevel) return;

    setLogLevel(level);
    this.__logLevel = level;
  }

  private async __addTestInvitation(values: any): Promise<unknown> {
    if (!this.__db || !this.__auth || !this.__dialog || !this.__dir?.me || !this.__communcation) return;

    try {
      const person = await this.__db.get<Person>(`people.${this.__dir.me.id}`);
      if (!person) throw new Error('Unable to find person');

      const companiesQuery = await this.__db.query('company', { id: { $eq: person.companyId } });
      const companies = companiesQuery.results;
      if (companies.length < 1) throw new Error('Can not create a test invitation with no companies');
      const companiesOptions = companies.map((c) => {
        return {
          value: c.id,
          label: c.name,
        };
      });

      const dialog = (await this.__dialog.open(
        {
          title: 'Add Test Invitation',
          description: 'Enter inviter & invitee email',
        },
        LtnDialogService.parseInputSchema({
          sections: {
            info: {
              title: 'Emails',
              fields: {
                inviteeName: {
                  type: DialogInputType.TEXT,
                  label: 'Invitee Name',
                  default: values.inviteeName || '',
                },
                inviteeEmail: {
                  type: DialogInputType.TEXT,
                  label: 'Invitee Email',
                  default: values.inviteeEmail || '',
                },
                inviteeCompanyName: {
                  type: DialogInputType.TEXT,
                  label: 'Invitee Company Name',
                  default: values.inviteeCompanyName || '',
                },
                company: {
                  type: DialogInputType.SELECT,
                  label: 'Company',
                  default: values.company || '',
                  options: companiesOptions,
                },
                redirect: {
                  type: DialogInputType.TOGGLE,
                  label: 'Redirect to Invitation Link',
                  default: values.redirect || false,
                },
              },
            },
          },
        }),
      )) as any;

      values = dialog.values.info;
      if (!values.inviteeName || !values.inviteeEmail || !values.inviteeCompanyName || !values.company)
        throw new ErrorDialogInvalid('Missing input');

      const output = await callLambda(
        'onboarding/create-invitation',
        'POST',
        this.__auth.token,
        JSON.stringify({
          redirect: values.redirect,
          userId: person.userId,
          oauthApp: sessionStorage.getItem('oauthApp'),
          companyId: values.company,
          invitations: [
            {
              inviteeName: values.inviteeName,
              inviteeEmail: values.inviteeEmail,
              inviteeCompanyName: values.inviteeCompanyName,
            },
          ],
        }),
      );

      sessionStorage.removeItem('oauthApp');
      sessionStorage.removeItem('userToken');
      const res = output.res as unknown as { url: string };
      window.location.href = res.url;
    } catch (err) {
      if (err instanceof ErrorDialogClosed) {
        logger.info('Dialog was closed early', err.schema);
      } else if (err instanceof ErrorDialogInvalid) {
        this.__toast?.queueToast({
          label: 'Please fill in all required fields: invitee name, email, company name and company.',
          timeout: 4000,
        });
        return await this.__addTestInvitation(values);
      } else {
        logger.error(err);
        this.__toast?.queueToast({
          label: err instanceof Error ? err.message : 'Something went wrong creating the test company.',
          timeout: 4000,
        });
      }
    }
  }

  private async __addTestCompany(values: any): Promise<unknown> {
    if (!this.__db || !this.__dialog || !this.__dir?.me) return;

    try {
      const profileOptions = [
        {
          value: 'PRIVATE',
          label: 'Private',
        },
        {
          value: 'PUBLIC',
          label: 'Public',
        },
      ];
      const typeOptions = [
        {
          value: 'PUBLIC_LIMITED_COMPANY',
          label: 'Public Limited Company',
        },
        {
          value: 'PRIVATE_LIMITED_COMPANY',
          label: 'Private Limited Company',
        },
        {
          value: 'PRIVATE_UNLIMITED_COMPANY',
          label: 'Private Unlimited Company',
        },
        {
          value: 'INTERNATIONAL_COMPANY',
          label: 'International Company',
        },
      ];
      const dialog = (await this.__dialog.open(
        {
          title: 'Add Test Company',
          description: 'Enter company info',
        },
        LtnDialogService.parseInputSchema({
          sections: {
            info: {
              title: 'Company',
              fields: {
                name: {
                  type: DialogInputType.TEXT,
                  label: "Company's Name",
                  default: values.name || 'Data People Connected Limited',
                },
                profile: {
                  type: DialogInputType.SELECT,
                  label: "Company's profile",
                  default: values.profile || '',
                  options: profileOptions,
                },
                companiesHouseNumber: {
                  type: DialogInputType.NUMBER,
                  label: 'CH number',
                  default: values.companiesHouseNumber || '86288198',
                },
                type: {
                  type: DialogInputType.SELECT,
                  label: "Company's type",
                  default: values.type || '',
                  options: typeOptions,
                },
                incorporatedOn: {
                  type: DialogInputType.DATE,
                  label: 'Incorporated On',
                  default: values.incorporatedOn || Sugar.Date.create('21/09/2009'),
                },
                address1: {
                  type: DialogInputType.TEXT,
                  label: 'Address 1',
                  default: values.address1 || '19 Old Hall Street',
                },
                address2: {
                  type: DialogInputType.TEXT,
                  label: 'Address 2',
                  default: values.address2 || '',
                },
                postcode: {
                  type: DialogInputType.TEXT,
                  label: 'Postcode',
                  default: values.postcode || 'L3 9JQ',
                },
                city: { type: DialogInputType.TEXT, label: 'City', default: values.city || 'Liverpool' },
                county: {
                  type: DialogInputType.TEXT,
                  label: 'County',
                  default: values.county || 'Merseyside',
                },
                country: {
                  type: DialogInputType.TEXT,
                  label: 'Country',
                  default: values.country || 'United Kingdom',
                },
              },
            },
          },
        }),
      )) as any;

      // Create a test company
      const company: ButtressEntity = this.__db.createObject('company');
      values = dialog.values.info;

      const validInput = Object.keys(values).every((key) => {
        if (key.toUpperCase() === 'ADDRESS2') return true;
        return !values[key] ? false : true;
      });

      if (!validInput) {
        throw new Error('Please fill all required fields for the company');
      }

      const companyReq = await this.__db.query('company', {
        name: {
          $eq: values.name,
        },
      });
      const existingCompany = companyReq?.results;
      if (existingCompany.length > 0) {
        throw new Error('Company with the same name already exists');
      }

      company.name = values.name;
      company.profile = values.profile;
      company.companiesRegistrar.push({
        name: 'COMPANIES_HOUSE',
        id: values.companiesHouseNumber,
        companyName: values.name,
        type: values.type,
        incorporatedOn: Sugar.Date.create(values.incorporatedOn),
        registeredAddress: {
          address1: values.address1,
          address2: values.address2,
          postcode: values.postcode,
          city: values.city,
          county: values.county,
          country: values.country,
        },
      });

      await this.__db.create('company', company);
      // Admin tokens only: user-access doesn't let a user write their own companyId
      await this.__db.set(`people.${this.__dir.me.id}.companyId`, company.id);
      await this.__db.nextIdle('people');
      await this.__dir.selectCompanyProfile(company.id);
      this.__toast?.queueToast({ label: `Test company "${company.name}" created and selected.`, timeout: 4000 });
    } catch (err) {
      if (err instanceof ErrorDialogClosed) {
        logger.info('Dialog was closed early', err.schema);
      } else if (err instanceof ErrorDialogInvalid) {
        this.__toast?.queueToast({
          label: 'Please fill in all required fields for the company.',
          timeout: 4000,
        });
        return await this.__addTestCompany(values);
      } else {
        logger.error(err);
        this.__toast?.queueToast({
          label: err instanceof Error ? err.message : 'Something went wrong creating the test company.',
          timeout: 4000,
        });
      }
    }
  }

  private async __transferHbar() {
    if (!this.__dir?.me || !this.__dir.company || !this.__workflowService) return;

    try {
      const workflowSchema = await this.__workflowService.findWorkflowSchema('@ns/transfer-node-cash-to-company');
      if (!workflowSchema) throw new Error('Unable to find workflow schema, @ns/transfer-node-cash-to-company');

      const results = await this.__workflowService.createWorkflowFromSchema(
        workflowSchema,
        'launcher',
        { COMPANY_ID: this.__dir.company.id, ASSIGNED_TO: this.__dir.me.id },
        { name: 'Select a company to transfer nodeCash to' },
      );

      if (!results.res) throw new Error('Unable to find request result res');
      if (!results.res.message || !Array.isArray(results.res.message))
        throw new Error('Unable to find request result res message');

      let jobCreationAction = results.res.message.find((m: any) => m.action.toUpperCase() === 'JOB_CREATED');
      if (!jobCreationAction) throw new Error('Could not find job creation action');
      jobCreationAction = jobCreationAction as { action: string; id: string };

      viewPath(`/feed/job/${jobCreationAction.id}`);
    } catch (err) {
      logger.error('Error transferring HBar', err);
      this.__toast?.queueToast({
        label: err instanceof Error ? err.message : 'Something went wrong starting the HBar transfer.',
        timeout: 4000,
      });
    }
  }

  private async __viewInternalPLTopic() {
    if (!this.__db) throw new Error('Missing required db service');
    if (!this.__internalPlTopic) {
      const internalPLTopicQueryCall = await this.__db.query('plTopic', {
        type: {
          $eq: 'INTERNAL',
        },
      });
      const results = internalPLTopicQueryCall.results as unknown as PublicLedgerTopic[];
      if (results.length > 0) [this.__internalPlTopic] = results;
    }

    if (!this.__internalPlTopic || !this.__internalPlTopic.url) {
      this.__toast?.queueToast({
        label: 'Could not find url for the internal public ledger topic',
        timeout: 4000,
      });
      return;
    }

    window.open(this.__internalPlTopic.url, '_blank');
  }

  private async __createTestEvents() {
    if (!this.__auth) return;

    try {
      await callLambda('debug/create-test-events', 'POST', this.__auth.token, JSON.stringify({ source: 'dev-tools' }));
      this.__toast?.queueToast({ label: 'Created 100 test events.', timeout: 4000 });
    } catch (err) {
      logger.error('Error creating test events', err);
      this.__toast?.queueToast({
        label: err instanceof Error ? err.message : 'Something went wrong creating test events.',
        timeout: 4000,
      });
    }
  }

  private __selectTestNotificationPriority(ev: Event) {
    const priority = (ev.target as HTMLElement & { value: string }).value;
    if (isNotificationPriority(priority)) this.__testNotificationPriority = priority;
  }

  private async __createTestNotification() {
    if (!this.__auth) return;

    // Notifications are never created by a direct client write — same reason `event` isn't
    // either. Routed through a dedicated, narrowly-scoped debug lambda (dev/test/staging only)
    // rather than this panel writing to the `notification` collection itself.
    //
    try {
      await callLambda(
        'debug/create-test-notification',
        'POST',
        this.__auth.token,
        JSON.stringify({ priority: this.__testNotificationPriority }),
      );
      this.__toast?.queueToast({
        label: `Created a test notification (${this.__testNotificationPriority}).`,
        timeout: 4000,
      });
    } catch (err) {
      logger.error('Error creating test notification', err);
      this.__toast?.queueToast({
        label: err instanceof Error ? err.message : 'Something went wrong creating the test notification.',
        timeout: 4000,
      });
    }
  }

  render() {
    return html`
      <div class="wrapper layout vertical" role="region" aria-labelledby="dev-tools-heading">
        <h2 id="dev-tools-heading">Dev Tools</h2>

        <section>
          <h3>Build</h3>
          <dl class="row-list m-0">
            <div class="row">
              <dt>Mode</dt>
              <dd class="value">${this.__appMode ?? '—'}</dd>
            </div>
            <div class="row">
              <dt>Version</dt>
              <dd class="value">${this.__appVersion ?? '—'}</dd>
            </div>
            <div class="row">
              <dt>Built</dt>
              <dd class="value">${this.__buildDate ?? '—'}</dd>
            </div>
          </dl>
        </section>

        <section>
          <h3>Session</h3>
          <dl class="row-list m-0">
            <div class="row">
              <dt>Person ID</dt>
              <dd class="value">${this.__personId ?? '—'}</dd>
            </div>
            <div class="row">
              <dt>Company</dt>
              <dd class="value">${this.__companyName ?? '—'}</dd>
            </div>
            <div class="row">
              <dt>Company ID</dt>
              <dd class="value">${this.__companyId ?? '—'}</dd>
            </div>
          </dl>
        </section>

        <section>
          <h3>Logging</h3>
          <wa-checkbox .checked=${this.__loggingEnabled} @change=${this.__toggleLogging}>
            Console logging enabled
          </wa-checkbox>
          <wa-select class="panel-select" label="Log level" .value=${this.__logLevel} @change=${this.__selectLogLevel}>
            ${LOG_LEVELS.map(
              (level) => html`
                <wa-option value=${level}>${level}</wa-option>
              `,
            )}
          </wa-select>
        </section>

        <section>
          <h3>Test personas</h3>
          <ns-test-persona-switcher></ns-test-persona-switcher>
        </section>

        <section>
          <h3>Actions</h3>
          <div class="actions layout vertical">
            <wa-button class="raised black-bg" variant="neutral" @click="${this.__addTestInvitation}">
              Create a test invitation
            </wa-button>
            <wa-button class="raised black-bg" variant="neutral" @click="${this.__addTestCompany}">
              Create a test company
            </wa-button>
            <wa-button class="raised black-bg" variant="neutral" @click="${this.__transferHbar}">
              Transfer HBar to a company
            </wa-button>
            <wa-button class="raised black-bg" variant="neutral" @click="${this.__viewInternalPLTopic}">
              View Internal Public Ledger Topic
            </wa-button>
            <wa-button class="raised black-bg" variant="neutral" @click="${this.__createTestEvents}">
              Create test events
            </wa-button>
            <wa-select
              class="panel-select"
              label="Test notification priority"
              .value=${this.__testNotificationPriority}
              @change=${this.__selectTestNotificationPriority}
            >
              ${NOTIFICATION_PRIORITIES.map(
                (priority) => html`
                  <wa-option value=${priority}>${priority}</wa-option>
                `,
              )}
            </wa-select>
            <wa-button class="raised black-bg" variant="neutral" @click="${this.__createTestNotification}">
              Create a test notification
            </wa-button>
          </div>
        </section>
      </div>
    `;
  }
}
